/**
 * Apply modal
 *
 * Workday's own apply flow cannot be embedded — it sends
 * x-frame-options: DENY — so this mirrors its "Start Your Application"
 * chooser and hands off to the matching Workday screen in a new tab.
 *
 * @package Interact_26
 */

import { type CareersConfig } from './types';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const WORKDAY_APPLY_PATH = /\/apply$/;
const BODY_OPEN_CLASS = 'careers-apply-modal-open';

const CLOSE_SVG = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>`;

export type ApplyOption = { label: string; url: string };

let modal: HTMLElement | null = null;
let panel: HTMLElement | null = null;
let titleEl: HTMLElement | null = null;
let listEl: HTMLElement | null = null;
let lastFocused: HTMLElement | null = null;
let isOpen = false;

/**
 * Workday's three entry points, derived from the posting's apply URL.
 *
 * Returns null when the URL is not a Workday apply link — manual jobs carry
 * arbitrary application links, so their button stays an ordinary link.
 */
export const getApplyOptions = (url: string, config: CareersConfig): ApplyOption[] | null => {
  if (!url || !WORKDAY_APPLY_PATH.test(url)) {
    return null;
  }

  return [
    { label: config.i18n.applyAutofill, url: `${url}/autofillWithResume` },
    { label: config.i18n.applyManually, url: `${url}/applyManually` },
    { label: config.i18n.applyLastApplication, url: `${url}/useMyLastApplication` },
  ];
};

/**
 * Close the modal and hand focus back to whatever opened it.
 */
const closeModal = (): void => {
  if (!modal || !isOpen) {
    return;
  }

  isOpen = false;
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  modal.hidden = true;
  document.body.classList.remove(BODY_OPEN_CLASS);

  lastFocused?.focus();
  lastFocused = null;
};

/**
 * Keep Tab inside the panel while the modal is open.
 */
const trapFocus = (event: KeyboardEvent): void => {
  if (!panel) {
    return;
  }

  const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

  if (focusables.length === 0) {
    return;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

/**
 * Build the modal shell once and reuse it for every job.
 */
const buildModal = (config: CareersConfig): void => {
  modal = document.createElement('div');
  modal.className = 'careers-apply-modal';
  modal.setAttribute('aria-hidden', 'true');
  modal.hidden = true;

  const backdrop = document.createElement('div');
  backdrop.className = 'careers-apply-modal__backdrop';
  backdrop.addEventListener('click', closeModal);

  panel = document.createElement('div');
  panel.className = 'careers-apply-modal__panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-labelledby', 'careers-apply-modal-heading');
  panel.tabIndex = -1;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'careers-apply-modal__close';
  close.setAttribute('aria-label', config.i18n.applyClose);
  close.innerHTML = CLOSE_SVG;
  close.addEventListener('click', closeModal);

  const heading = document.createElement('h2');
  heading.id = 'careers-apply-modal-heading';
  heading.className = 'careers-apply-modal__heading typography typo-h3 typo-font-heading';
  heading.textContent = config.i18n.applyHeading;

  titleEl = document.createElement('p');
  titleEl.className = 'careers-apply-modal__job';

  const intro = document.createElement('p');
  intro.className = 'careers-apply-modal__intro';
  intro.textContent = config.i18n.applyIntro;

  listEl = document.createElement('div');
  listEl.className = 'careers-apply-modal__options';

  const note = document.createElement('p');
  note.className = 'careers-apply-modal__note';
  note.textContent = config.i18n.applyNote;

  panel.append(close, heading, titleEl, intro, listEl, note);
  modal.append(backdrop, panel);
  document.body.appendChild(modal);

  document.addEventListener('keydown', (event) => {
    if (!isOpen) {
      return;
    }

    if ('Escape' === event.key) {
      closeModal();
    } else if ('Tab' === event.key) {
      trapFocus(event);
    }
  });
};

/**
 * Open the chooser for one job.
 */
export const openApplyModal = (
  options: ApplyOption[],
  jobTitle: string,
  config: CareersConfig,
  trigger?: HTMLElement | null,
): void => {
  if (!modal) {
    buildModal(config);
  }

  if (!modal || !panel || !listEl || !titleEl) {
    return;
  }

  // Safari does not focus a link when it is clicked, so the opener is passed
  // in rather than read from document.activeElement.
  lastFocused = trigger ?? (document.activeElement as HTMLElement | null);
  titleEl.textContent = jobTitle;
  listEl.replaceChildren();

  options.forEach((option, index) => {
    const link = document.createElement('a');
    link.href = option.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.className =
      0 === index
        ? 'interact-button button--primary button--large'
        : 'interact-button button--secondary button--large';
    link.textContent = option.label;
    link.addEventListener('click', closeModal);
    listEl?.appendChild(link);
  });

  isOpen = true;
  modal.hidden = false;
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add(BODY_OPEN_CLASS);

  // Defer so the CSS transition catches the change.
  requestAnimationFrame(() => {
    modal?.classList.add('is-open');
    panel?.focus();
  });
};
